import { Check } from '@sparkware/app-utils';
import {ID_VERIFICATION_STATUS} from '../../../../oauth/oauth_const';
import {TimeSpan} from "@sparkware/app-utils";
import OptionsComponentController from '../../../../../components/options/options_component_controller';
import COMBaseRegulation from "../com_base_regulation";
import { TSR_TYPE } from '../../../../../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/time-spent-reminder-options';
import {KICK_REASON} from "../../../../player_messages_service/player_messages_consts";
import Underscore from "../../../../../utils/underscore";
import {
    DISPLAY_MESSAGE_IDS,
    DISPLAY_MESSAGE_NAMES
} from "../../../../../../../common/const/casinofied-analytics-consts";

interface AccountData {
    IdVerificationStatus: number,
    AddressVerificationStatus: number,
    RealityCheckDataIntervalInMins: number
}

export default class UKRegulation extends COMBaseRegulation {
    static CLASS = 'UKRegulation';

    private _getAccountData(): AccountData {
        return this.serviceFactory.userService().getAccountData() as AccountData;
    }

    isAnonymousPlayAllowed() : boolean {
        if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return false;
        }

        return this._getAccountData().IdVerificationStatus === ID_VERIFICATION_STATUS.AGE_VERIFIED;
    }

    forceHideCloseButtonInMembership() {
        return true;
    }

    forceDemoButton(): boolean {

        return this.isAnonymousPlayAllowed() && this._getAccountData().AddressVerificationStatus !== ID_VERIFICATION_STATUS.ADDRESS_VERIFIDED;
    }

    protected realityCheckEnabled(): boolean {
        return true;
    }

    getRealityCheckInterval(): TimeSpan {
        if (this.serviceFactory.skinService().useControlCenterForReminderInterval()) {
            if (this._getAccountData() && !Check.isNullOrUndefined(this._getAccountData().RealityCheckDataIntervalInMins)) {
                return TimeSpan.fromMinutes(this._getAccountData().RealityCheckDataIntervalInMins);
            }
        }
        return this.serviceFactory.componentFactoryService().createComponent(OptionsComponentController).getReminderInterval();
    }

    async updateRealityCheckInterval(realityCheckIntevalMin: number): Promise<void> {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return Promise.reject();
        }

        // Update reminders
        // Set the reminder interval in account details and control center
        if (this.serviceFactory.skinService().useControlCenterForReminderInterval()) {
            let updateRealityCheckDataUrl = this.serviceFactory.userService().getUpdateRealityCheckDataUrl();
            try {
                // updates reality check in Control Center. Use this endpoint to migrate existing reality check
                await this.serviceFactory.http2Service().post(updateRealityCheckDataUrl, { realityCheckIntevalMin });
            } catch (error) {
                this.logger.error("Could not call update reality check to update the interval. url: " + updateRealityCheckDataUrl, error);
            }
            // when the call is done, update the account details for the current session
            const accountData = this.serviceFactory.userService().getAccountData();
            if (accountData && !Check.isNullOrUndefined(accountData.RealityCheckDataIntervalInMins)) {
                accountData.RealityCheckDataIntervalInMins = realityCheckIntevalMin;
                this.serviceFactory.userService().updateTokenData("accountData", accountData);
            }
        } else {
            // Set the reminder that use the setting from localStorage
            OptionsComponentController.create().load('ReminderSettings').set(realityCheckIntevalMin);
        }
        return Promise.resolve();
    }

    async attemptRealityCheckIntervalMigrationFromLocalStorageToControlCenter(): Promise<void> {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return Promise.reject();
        }
        if (this.serviceFactory.skinService().useControlCenterForReminderInterval()) {
            // when using Control Center for reminder interval, first we check if the data was migrated from Storage to Control Center.
            let controlCenterReminderInterval: TimeSpan | undefined = undefined;
            if (this._getAccountData() && !Check.isNullOrUndefined(this._getAccountData().RealityCheckDataIntervalInMins)) {
                controlCenterReminderInterval = TimeSpan.fromMinutes(this._getAccountData().RealityCheckDataIntervalInMins);
            }

            if (controlCenterReminderInterval && controlCenterReminderInterval.totalMinutes > 0) {
                // 1. If data exists in Control center, ignore if anything exists in Local Storage. Attempt to delete from Local Storage
                OptionsComponentController.create().load('ReminderSettings').off();
                return Promise.resolve();
            }

            const localStorageReminderInterval: TimeSpan = this.serviceFactory.componentFactoryService().createComponent(OptionsComponentController).getReminderInterval();
            if (localStorageReminderInterval.totalMinutes > 0) {
                // 2. If data does not exists in Control Center, but exists in Local Storage, migrate:
                let updateRealityCheckDataUrl = this.serviceFactory.userService().getUpdateRealityCheckDataUrl();
                try {
                    await this.serviceFactory.http2Service().post(updateRealityCheckDataUrl, { realityCheckIntevalMin: localStorageReminderInterval.totalMinutes });
                    // and remove data from Local Storage with:
                    OptionsComponentController.create().load('ReminderSettings').off();

                    // when the call is done, update the account details for the current session
                    const accountData = this.serviceFactory.userService().getAccountData();
                    if (accountData && !Check.isNullOrUndefined(accountData.RealityCheckDataIntervalInMins)) {
                        accountData.RealityCheckDataIntervalInMins = localStorageReminderInterval.totalMinutes;
                        this.serviceFactory.userService().updateTokenData("accountData", accountData);
                    }
                } catch (error) {
                    this.logger.error("Could not call update reality check to update the interval. url: " + updateRealityCheckDataUrl, error);
                    return Promise.reject();
                }
            }
        }
        return Promise.resolve();
    }

    getRealityCheckIntervalExpectedByTheGame() {
        let interval = this.getRealityCheckInterval();
        return {
            timeSpan: interval,
            expectedValueByTheGame: Math.round(interval.totalSeconds)
        }
    }

    shouldHaveBetLimitsPerBrand(): boolean {
        return true;
    }

    showNetPosition(): boolean {
        return true;
    }

    sessionTimerEnabled() {
        const isMiniCip = this.serviceFactory.integrationService().isMiniCip();
        return !isMiniCip && (this.getRealityCheckInterval().totalMilliseconds > 0);
    }

    _handleRealityCheckMessage(sessionTimer) {
        this.serviceFactory.gameService().stopCurrentGameAutoplay();

        this.serviceFactory.orbitComponentRenderService().showTimeSpentReminderWithDropdown({
            timerType: TSR_TYPE.SESSION_TIMER,
            playingTime: this.getCurrentSessionTime()
        });
    }

    onKickEvent(kickParams) {
        super.onKickEvent(kickParams);

        if (kickParams.KickReasonId !== KICK_REASON.KickUserUkNightLimit) {
            return;
        }

        this.serviceFactory.gameService().closeRunningGame();

        if (this.serviceFactory.integrationService().isMiniOrbit()) {
            return; //On CIP8/CIS, the native app displays the message, we just need to soft kick the user.
        }

        const businessCorrelationId = Underscore.getGuid();
        const limitExpirationDate = new Date(kickParams.LimitExpirationDateInSeconds * 1000);
        const limitInHours = this.serviceFactory.timeService().findDifferenceBetweenDatesInHours(limitExpirationDate, new Date());
        //Looks like we lose 1 hour due to truncating and stuff, need to also check with real message to see if we need the +1
        const hoursString: string = (limitInHours + 1).toString();

        this.serviceFactory.casinofiedAnalyticsProxyService().messageDisplay({
            businessCorrelationID: businessCorrelationId,
            displayMessageName: DISPLAY_MESSAGE_NAMES.DISRUPTIVE_KICKOFF_MESSAGE_NAME,
            displayMessageID: DISPLAY_MESSAGE_IDS.nightLimitKick,
            correlationID: kickParams.CorrelationID
        });

        this.serviceFactory.messageManagerService().openMessage({
            title: this.serviceFactory.languageService().translation('night_limit_title'),
            text: this.serviceFactory.languageService().translation('night_limit_soft_kick_text', {
                hours: hoursString
            }),
            closeButton:false,
            hasDefaultButton: false,
            buttons: [
                {
                    name: this.serviceFactory.languageService().translation('night_limit_cta'),
                    callback: ()=> {
                        this.serviceFactory.casinofiedAnalyticsProxyService().messageActionTaken({
                            displayMessageName: DISPLAY_MESSAGE_NAMES.DISRUPTIVE_KICKOFF_MESSAGE_NAME,
                            actionType: "Confirmation",
                            correlationID: kickParams.CorrelationID,
                            businessCorrelationID: businessCorrelationId
                        });
                    },
                    isClosable: true
                }
            ]
        });
    }

    showNightLimitMessage(nightLimitExpirationDate: Date) {
        const locale = this.serviceFactory.skinService().getCulture();
        const hour = nightLimitExpirationDate.toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit',
        });
        const date = nightLimitExpirationDate.toLocaleDateString(locale);

        this.serviceFactory.messageManagerService().openMessage({
            title: this.serviceFactory.languageService().translation('night_limit_title'),
            text: this.serviceFactory.languageService().translation('night_limit_open_game_text', {
                hour: hour.toString(),
                date: date.toString(),
            }),
            closeButton:false,
            hasDefaultButton: false,
            buttons: [
                {
                    name: this.serviceFactory.languageService().translation('night_limit_cta'),
                    isClosable: true
                }]
        });
    }
}